/**
 * T18 race-end recap presentation timings. These are a UX concern (how fast the
 * staged recap animation plays), NOT a game rule — so they live in the client,
 * not the engine `config.ts`. Mirrors `RECAP_CONFIG` in the approved wireframe
 * (docs/wireframes/horror-race/states/race-recap.html).
 */
export interface RecapConfig {
  /** How long the ① board-ranking stage holds before ② reveal. */
  readonly rankingHoldMs: number;
  /** Gap between each player's ② sequential secret-bet reveal. */
  readonly revealStepMs: number;
  /** Duration of a single ③ garnet count-up (roulette settle). */
  readonly garnetRollMs: number;
  /** Gap between each player's ③ garnet tally. */
  readonly garnetStepGapMs: number;
  /** Gap between stages. */
  readonly stageGapMs: number;
}

export const RECAP_CONFIG: RecapConfig = {
  rankingHoldMs: 1800,
  revealStepMs: 700,
  garnetRollMs: 650,
  garnetStepGapMs: 450,
  stageGapMs: 500,
};

/**
 * T26 round-end recap presentation timings (ms, NORMAL speed). Mirrors
 * `ROUND_RECAP_CONFIG` in the approved wireframe
 * (docs/wireframes/horror-race/states/round-recap.html). The effective duration
 * of each beat is `base × speedScale` (see `ROUND_RECAP_SPEED`).
 */
export interface RoundRecapConfig {
  /** "N라운드 종료" declaration hold. */
  readonly declareEndMs: number;
  /** Zone + winner highlight (per zone). */
  readonly zoneHighlightMs: number;
  /** Pawn board-move transition. */
  readonly pawnMoveMs: number;
  /** Gap after a move before the next zone. */
  readonly zoneSettleMs: number;
  /** Hold for a tie / no-move zone. */
  readonly noMoveMs: number;
  /** "N라운드 완료" declaration hold. */
  readonly declareDoneMs: number;
  /** Rail order-transition (FLIP) on advance to the next round. */
  readonly railReorderMs: number;
}

export const ROUND_RECAP_CONFIG: RoundRecapConfig = {
  declareEndMs: 900,
  zoneHighlightMs: 600,
  pawnMoveMs: 650,
  zoneSettleMs: 350,
  noMoveMs: 700,
  declareDoneMs: 900,
  railReorderMs: 380,
};

/**
 * Scale factor applied to every round-recap beat to adjust the overall pace —
 * effective time = base × scale. A UI toggle is intentionally deferred ("나중에");
 * for now this is a config seam defaulting to NORMAL.
 */
export type RoundRecapSpeed = "NORMAL" | "FAST";

export const ROUND_RECAP_SPEED_SCALE: Readonly<
  Record<RoundRecapSpeed, number>
> = {
  NORMAL: 1.0,
  FAST: 0.45,
};

/** The active round-recap speed. (Toggle UI deferred — see above.) */
export const ROUND_RECAP_SPEED: RoundRecapSpeed = "NORMAL";
